import { X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import {
  CATEGORY_PALETTE,
  COLOUR_NAMES,
  DEFAULT_EMOJI,
  categoryName,
} from "../lib/category-icons.ts";
import type { PoiCategory } from "../lib/pois.ts";
import { ConfirmDialog, reason } from "../ui/index.ts";

/** What a category is, as the form sends it: always all three, so nothing is left to a default. */
export type CategoryFace = { name: string; emoji: string; color: string };

/** Faces to pick from, by kind: what a trip's places tend to be. Anything else is typed beside them. */
export const FACES = [
  "🍽️",
  "🌮",
  "🍕",
  "🍜",
  "🍔",
  "☕",
  "🍺",
  "🍸",
  "🥐",
  "🍦",
  "🛒",
  "🏛️",
  "⛪",
  "🏰",
  "🎭",
  "🖼️",
  "📸",
  "🌳",
  "🏖️",
  "⛰️",
  "🎡",
  "🎶",
  "⚽",
  "🥾",
  "🏨",
  "🚉",
  "🚌",
  "🛍️",
  "💊",
  DEFAULT_EMOJI,
] as const;

/** Characters as the server counts them (code points), so "⚽" is one and a flag is two. */
const chars = (s: string) => [...s].length;

/** What removing leaves behind, said before it is done: the places stay, with no category. */
export function leftBehind(places: number): string {
  if (places === 0) return "No place is in it.";
  return places === 1
    ? "Its 1 place stays, with no category."
    : `Its ${places} places stay, with no category.`;
}

/**
 * One category's form, in a panel of its own over the page, so the list behind it stays put.
 * `category` is the one being edited, or null for a new one, which starts on `colour`.
 *
 * Top to bottom: a live pin (the face and colour as the map will draw them), the name, the face
 * (a grid of buttons, and a field beside it for anything not in the grid), the colour, the actions,
 * and, ruled off at the bottom and only for a category that exists, Remove with what it leaves.
 *
 * Nothing is shown saved before the server says so: `onSave` and `onRemove` resolve once it has
 * answered, and the host closes the panel then; they reject when it refused, and the panel stays as
 * typed with the reason on it. Removing asks first.
 */
export function CategoryForm({
  category,
  colour,
  places,
  onSave,
  onRemove,
  onClose,
}: {
  category: PoiCategory | null;
  /** The colour a new category starts on. */
  colour: string;
  /** How many places the category holds, for what Remove says it leaves. */
  places: number;
  onSave: (face: CategoryFace) => Promise<void>;
  onRemove: () => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(category?.name ?? "");
  const [emoji, setEmoji] = useState(category?.emoji ?? DEFAULT_EMOJI);
  const [color, setColor] = useState(category?.color ?? colour);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const nameField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus goes into the panel and comes back to whatever opened it.
    const opener = document.activeElement as HTMLElement | null;
    (category === null ? nameField.current : panel.current)?.focus();
    return () => opener?.focus?.();
    // Only on opening: a category arriving later must not pull focus.
  }, []);

  const trimmed = name.trim();
  const face = emoji.trim();
  const problem =
    chars(face) > 16 || (emoji !== "" && chars(face) < 1)
      ? "An emoji is 1 to 16 characters."
      : chars(trimmed) > 40
        ? "A category is 1 to 40 characters."
        : null;
  const ready = problem === null && trimmed !== "" && face !== "";

  async function run(act: () => Promise<void>, failed: string) {
    setBusy(true);
    setError(null);
    try {
      await act();
    } catch (e) {
      console.error("places: a category edit did not reach the server", e);
      setError(`${failed} ${reason(e as Error)}`);
    } finally {
      setBusy(false);
    }
  }

  const save = () => {
    if (!ready || busy) return;
    void run(() => onSave({ name: trimmed, emoji: face, color }), "Not saved.");
  };

  const colourName = COLOUR_NAMES[color as keyof typeof COLOUR_NAMES] ?? "Custom";

  return (
    <>
      <div className="sheet-scrim">
        <div
          ref={panel}
          className="cat-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === "Escape" && !asking) onClose();
          }}
        >
          <header className="cat-panel-head">
            <h2 id={titleId}>{category === null ? "New category" : "Edit category"}</h2>
            <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
              <X size={20} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </header>

          <span
            className="cat-preview"
            role="img"
            aria-label={`Preview: ${face || "no face"} in ${colourName.toLowerCase()}`}
            style={{ background: color }}
          >
            {face}
          </span>

          <label className="cat-field">
            <span className="label">Name</span>
            <input
              ref={nameField}
              value={name}
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <div className="cat-field">
            <span className="label">Face</span>
            <div className="cat-faces">
              {FACES.map((f) => (
                <button
                  key={f}
                  type="button"
                  className="cat-face-option"
                  aria-label={`Use ${f}`}
                  aria-pressed={face === f}
                  onClick={() => setEmoji(f)}
                >
                  <span aria-hidden="true">{f}</span>
                </button>
              ))}
            </div>
            <label className="cat-field">
              <span className="small muted">Or type one</span>
              <input value={emoji} autoComplete="off" onChange={(e) => setEmoji(e.target.value)} />
            </label>
          </div>

          <div className="cat-field">
            <span className="label">Colour</span>
            <div className="cat-colours">
              {CATEGORY_PALETTE.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className="cat-colour"
                  aria-label={COLOUR_NAMES[hex]}
                  aria-pressed={color === hex}
                  onClick={() => setColor(hex)}
                >
                  <span style={{ background: hex }} aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          {problem && (
            <p role="alert" className="error small">
              {problem}
            </p>
          )}
          {error && (
            <p role="alert" className="error small">
              {error}
            </p>
          )}

          <div className="cat-actions">
            <button type="button" className="button" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="button primary"
              aria-busy={busy || undefined}
              disabled={busy || !ready}
              onClick={save}
            >
              Save
            </button>
          </div>

          {category !== null && (
            <div className="cat-remove">
              <p className="small muted">{leftBehind(places)}</p>
              <button
                type="button"
                className="button danger-outline"
                disabled={busy}
                onClick={() => setAsking(true)}
              >
                Remove {category.name}
              </button>
            </div>
          )}
        </div>
      </div>
      {category !== null && (
        <ConfirmDialog
          open={asking}
          title={`Remove ${category.emoji} ${categoryName(category.name)}? ${leftBehind(places)}`}
          confirmLabel="Remove"
          destructive
          onConfirm={() => {
            setAsking(false);
            void run(onRemove, "Not removed.");
          }}
          onCancel={() => setAsking(false)}
        />
      )}
    </>
  );
}
